/**
 * Deep-link + platform helpers for the minimal mobile web.
 *
 * The web Cuidari (Ariadne) is the "porta de entrada" for users who land
 * via a link, search or a shared milestone. When a logged-in user hits
 * a heavy surface (`/finance/bills`, `/historico`, `/finance/insights`,
 * `/profile`) on a small viewport, the canonical experience is the
 * native app. This module owns the two primitives that surface needs:
 *
 *   1. `getInstallPlatform()` — sniffs the User-Agent to pick between
 *      iOS / Android / desktop CTAs. Intentionally narrow: only the
 *      decision the install page renders. No heavy UA parsing dep.
 *
 *   2. `buildCuidariDeepLink(route)` — builds a `cuidari://<path>`
 *      universal-link URL. Falls back to the install page when the
 *      caller renders it as `href` (the OS shows the install page if
 *      the app is not installed; the install page itself is the safety
 *      net for desktop + uninstalled mobile).
 *
 * Both helpers are SSR-safe: they return sensible defaults when
 * `navigator` is not available (we treat that as "desktop" because the
 * test runner + future SSR want a deterministic default).
 *
 * NOTE: the iOS App Store listing is live (`APP_STORE_URL`), and
 * `PLAY_STORE_URL` points at the real package id — both constants are the
 * single source every surface picks up.
 */

export type InstallPlatform = 'ios' | 'android' | 'desktop'

interface DeepLinkOptions {
  /** Relative path inside the app, e.g. `/finance/bills` or `/profile`. */
  route?: string
  /** Query string (already encoded), without the leading `?`. */
  search?: string
}

/** iOS App Store listing (live). */
export const APP_STORE_URL =
  'https://apps.apple.com/br/app/cuidari/id6779703825'

/** Play Store listing (real package id). */
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=ai.francisquini.cuidari'

/** Web origin used for QR codes when sharing the install URL. */
export const INSTALL_URL_PATH = '/instalar'

const IOS_RE = /iphone|ipad|ipod/i
const ANDROID_RE = /android/i

/** Returns `'ios' | 'android' | 'desktop'`. SSR-safe (defaults to desktop). */
export function getInstallPlatform(userAgent?: string): InstallPlatform {
  const ua =
    userAgent ??
    (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent
      : '')
  if (!ua) return 'desktop'
  if (IOS_RE.test(ua)) return 'ios'
  if (ANDROID_RE.test(ua)) return 'android'
  return 'desktop'
}

/**
 * Builds a `cuidari://` universal-link URL. Caller usually wires this as
 * an `<a href>` — when the app is installed, the OS opens it directly;
 * when it is not, the browser surfaces a "no app installed" prompt.
 * Surfaces should always pair this with an "install" secondary CTA.
 */
export function buildCuidariDeepLink({
  route = '/',
  search,
}: DeepLinkOptions = {}): string {
  const path = route.startsWith('/') ? route.slice(1) : route
  const qs = search ? `?${search.replace(/^\?/, '')}` : ''
  return `cuidari://${path}${qs}`
}

/**
 * Picks the install-store URL for a given platform. Desktop returns the
 * web install page (no store target — desktop users either scan the QR
 * or share the install link with themselves).
 */
export function getStoreUrl(platform: InstallPlatform): string {
  if (platform === 'ios') return APP_STORE_URL
  if (platform === 'android') return PLAY_STORE_URL
  return INSTALL_URL_PATH
}
